import { useState } from "react";
import { InputGroup, Button, Form } from "react-bootstrap";
import {
    useFloating,
    autoUpdate,
    offset,
    flip,
    shift,
    useClick,
    useDismiss,
    useRole,
    useInteractions,
    FloatingFocusManager
  } from '@floating-ui/react';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import { nl } from 'date-fns/locale';
import { Locale, format } from 'date-fns';
import styles from './floating-datepicker.module.scss';
import withDefaultProps from "with-default-props";

interface Props {
    date?: Date | undefined;
    placeholder?: string;
    minDate?: Date | undefined;
    maxDate?: Date | undefined;
    dateChange?: (date: Date | undefined) => void;
    locale: Locale;
    format: string;
}

const defaultProps: Props = {
    locale: nl,
    format: 'PPP'
}

const FloatingDatepicker = (props: Props) => {
    const [ isOpen, setIsOpen ] = useState<boolean>(false);

    const {refs, floatingStyles, context} = useFloating({
        open: isOpen,
        onOpenChange: setIsOpen,
        middleware: [offset({ mainAxis: 10, crossAxis: props.date ? 15 : 0 }), flip(), shift()],
        whileElementsMounted: autoUpdate,
    });

    const click = useClick(context);
    const dismiss = useDismiss(context);
    const role = useRole(context);
    
    // Merge all the interactions into prop getters
    const {getReferenceProps, getFloatingProps} = useInteractions([
        click,
        dismiss,    
        role,
    ]);

    const handleDateChange = (d: Date | undefined): void => {
        if (props.dateChange) {
            props.dateChange(d);
        }

        setIsOpen(false);
    }

    const handleReset = (): void => {
        if (props.dateChange) {
            props.dateChange(undefined);
        }
    }
    
    return (
        <>
            <InputGroup>
                <Form.Control
                    readOnly={true}
                    placeholder={props.placeholder}
                    value={props.date ? format(props.date, props.format, { locale: props.locale }) : ''}
                    ref={refs.setReference} {...getReferenceProps()}
                />
                {
                    props.date && (
                        <Button className={styles.reset_button} variant="outline-secondary" id="reset-button" onClick={handleReset}>
                            X
                        </Button>
                    )
                }
            </InputGroup>

            {
                isOpen && (
                    <FloatingFocusManager context={context} modal={true}>
                        <div
                            className={styles.container}
                            ref={refs.setFloating}
                            style={floatingStyles}
                            {...getFloatingProps()}
                        >
                             <DayPicker
                                mode="single"
                                captionLayout="dropdown-buttons"
                                locale={props.locale}
                                selected={props.date}
                                onSelect={handleDateChange}
                                // Default dates are needed to support dropdowns for month/year
                                fromDate={props.minDate ?? new Date(1990, 0, 1)}
                                toDate={props.maxDate ?? new Date()}
                            />
                        </div>
                    </FloatingFocusManager>
                )
            }
        </>
        
    )
}

export default withDefaultProps(FloatingDatepicker, defaultProps)